import { useCallback } from 'react';
import { enumValue, getGapTypeBySection, gapIsAddressed, isInRolledUpStatus } from '~/util';
import { PatientCacheRef } from './usePatientService';

interface GapStatItem {
    status: number;
    count: number;
}

interface SummaryStatItem {
    status: 'percent';
    count: string;
}

type StatItem = GapStatItem | SummaryStatItem;

type GapSection = 'foc_gaps' | 'quality_gaps' | 'risk_gaps';

export type PatientStats = Record<GapSection, StatItem[]>;

const statMap: Record<GapSection, (number | false)[]> = {
    foc_gaps: [
        enumValue(['gap', 'status', 'Pending']),
        enumValue(['gap', 'status', 'Addressed']),
        enumValue(['gap', 'status', 'Under Review']),
    ],
    quality_gaps: [
        enumValue(['gap', 'status', 'Pending']),
        enumValue(['gap', 'status', 'Addressed']),
        enumValue(['gap', 'status', 'Fail']),
        enumValue(['gap', 'status', 'Under Review']),
    ],
    risk_gaps: [
        enumValue(['gap', 'status', 'Pending']),
        enumValue(['gap', 'status', 'Addressed']),
        enumValue(['gap', 'status', 'Fail']),
        enumValue(['gap', 'status', 'Under Review']),
    ],
};

export const useGetStats = (patientCache: PatientCacheRef) => {
    const getStats = useCallback(
        (id: number): PatientStats => {
            const patient = patientCache.current[id];
            const gapStats = {} as Record<GapSection, GapStatItem[]>;

            (Object.keys(statMap) as GapSection[]).forEach((section) => {
                gapStats[section] = statMap[section].map((x) => {
                    const xInt = Number(x);
                    return {
                        status: xInt,
                        count: patient[section].filter(
                            (y: RiskGapType | GapType) =>
                                isInRolledUpStatus(getGapTypeBySection(section), xInt, y.status) &&
                                !y.is_added
                        ).length,
                    };
                });
            });

            // If there are siblings, we count one for each unique status of the siblings.
            // If the primary is addressed, all siblings will also be addressed, so we
            // have already counted what we need.
            // If the primary is any other status, the siblings will all be addressed, so
            // we can just increment addressed by one for each case of siblings exist and
            // primary gap status is not addressed.
            const addressedStatus = enumValue(['gap', 'status', 'Addressed']) as number;
            const addressedItem = gapStats.risk_gaps.find((x) => x.status === addressedStatus)!;
            addressedItem.count += patient.risk_gaps.filter(
                (x: RiskGapType) => x?.siblings && x.siblings.length > 0 && !gapIsAddressed(x)
            ).length;

            // Get the sum, but remember that Failed is already added in Pending, so don't
            // count it twice!
            const failedStatus = enumValue(['gap', 'status', 'Fail']) as number;
            const riskGapTotal = gapStats.risk_gaps.reduce(
                (acc, x) => (x.status === failedStatus ? acc : acc + x.count),
                0
            );

            const percentItem: SummaryStatItem = {
                status: 'percent',
                count:
                    riskGapTotal === 0
                        ? '0%'
                        : `${Math.round((addressedItem.count / riskGapTotal) * 100)}%`,
            };

            return {
                foc_gaps: gapStats.foc_gaps,
                quality_gaps: gapStats.quality_gaps,
                risk_gaps: [...gapStats.risk_gaps, percentItem],
            };
        },
        [patientCache]
    );

    return getStats;
};
